import React from 'react';
import type { ComponentProps } from 'react';
import InputText from '../InputText/InputText';
import useInputPassword from './useInputPassword';

/**
 * InputPassword - Password field built on top of `InputText` v2.
 *
 * Manages the visibility toggle (eye button) internally and forwards the rest
 * of the props to `InputText`. It is a "naked" atom like `InputText`: it does
 * not render a label or validation messages; wrap it in `FormField` for those.
 */
type InputPasswordProps = Omit<ComponentProps<typeof InputText>, 'type' | 'icon' | 'onClickIcon'> & {
  /**
   * Shows the eye button to toggle password visibility.
   * `false` renders a plain masked field (no toggle). Defaults to `true`.
   */
  hasVisibilityToggle?: boolean;
};

const InputPassword = React.forwardRef<HTMLInputElement, InputPasswordProps>(
  ({ hasVisibilityToggle = true, iconAriaLabel, ...props }, ref) => {
    const toggleProps = useInputPassword({ hasVisibilityToggle, iconAriaLabel });

    return <InputText ref={ref} {...props} {...toggleProps} />;
  }
);

InputPassword.displayName = 'InputPassword'; // NOSONAR - React displayName, not a hard-coded password

// Opt in to receiving `validationState` from `FormField` (like `InputText`),
// so the inline validation icon renders inside the inner `InputText`.
(InputPassword as typeof InputPassword & { supportsValidationState: boolean }).supportsValidationState = true;

export default InputPassword;
